/* Change-reason prompt — required reason (quick-pick chips or own words) for deletes,
 * cancels and key-field edits in the Order Register. Recorded in the audit log. */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ClipboardList } from "lucide-react";

export const REASON_CHIPS = ["Sales team request", "Duplicate entry", "Wrong entry", "Client cancelled", "Address correction", "Other"];

export const reasonText = (chip, note) => {
  const n = (note || "").trim();
  if (!chip || chip === "Other") return n;
  return n ? `${chip} — ${n}` : chip;
};

// Chips + note box; onChange(fullReasonText).
export const ReasonChips = ({ onChange, testId = "reason" }) => {
  const [chip, setChip] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { onChange(reasonText(chip, note)); }, [chip, note, onChange]);
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5">
        {REASON_CHIPS.map(c => (
          <button key={c} type="button" onClick={() => setChip(x => (x === c ? "" : c))}
            data-testid={`${testId}-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${chip === c ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            {c}
          </button>
        ))}
      </div>
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={400}
        placeholder={chip && chip !== "Other" ? "Add details (optional) — e.g. who asked, ticket #" : "Write the reason in your own words"}
        className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
        data-testid={`${testId}-note`} />
    </div>
  );
};

let _open = null;
// Promise<string|null> — null when the user cancels.
export const askChangeReason = (opts) => new Promise(resolve => {
  if (!_open) { resolve(window.prompt(opts?.title || "Reason") || null); return; }
  _open({ ...opts, resolve });
});

export const ReasonPromptHost = () => {
  const [req, setReq] = useState(null);
  const [val, setVal] = useState("");
  useEffect(() => { _open = (r) => { setVal(""); setReq(r); }; return () => { _open = null; }; }, []);
  const close = (v) => { req?.resolve(v); setReq(null); };
  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) close(null); }}>
      <DialogContent className="max-w-md" data-testid="change-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="w-4 h-4 text-indigo-600" /> {req?.title || "Reason for this change"}
          </DialogTitle>
        </DialogHeader>
        {req?.detail && <div className="rounded-md bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 whitespace-pre-line" data-testid="change-reason-detail">{req.detail}</div>}
        {req && <ReasonChips onChange={setVal} testId="change-reason" />}
        <p className="text-[11px] text-slate-500">Saved in the audit log with your name and time.</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => close(null)} data-testid="change-reason-cancel">Cancel</Button>
          <Button size="sm" disabled={!val.trim()} onClick={() => close(val.trim())} className="bg-indigo-600 hover:bg-indigo-700" data-testid="change-reason-confirm">
            {req?.confirmLabel || "Save with reason"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
